import { useEffect, useState } from "react";
import axios from "axios";
import { Overline, Section, Pill } from "../components/Bits";
import { MapPin, Clock, ArrowRight, Truck, Store, Filter } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom icons for trucks (mustard) and kiosks (green)
const makeIcon = (color, active) => L.divIcon({
  className: "gm-leaflet-icon",
  iconSize: [44, 44],
  iconAnchor: [22, 44],
  popupAnchor: [0, -40],
  html: `
    <div style="position:relative;transform:translateY(${active ? "-4px" : "0"});transition:transform .2s;">
      <div style="
        width:40px;height:40px;border-radius:50%;
        background:${color};
        display:flex;align-items:center;justify-content:center;
        box-shadow:0 6px 16px rgba(15,56,36,${active ? "0.45" : "0.25"});
        border:3px solid white;
        ${active ? "outline:3px solid #E5B03C;outline-offset:1px;" : ""}
      ">
        <svg viewBox='0 0 24 24' width='18' height='18' fill='none' stroke='white' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'>
          <path d='M10 17h4V5H2v12h3'/><path d='M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h2'/>
          <circle cx='7.5' cy='17.5' r='2.5'/><circle cx='17.5' cy='17.5' r='2.5'/>
        </svg>
      </div>
      <div style="
        position:absolute;left:50%;bottom:-2px;transform:translateX(-50%) rotate(45deg);
        width:10px;height:10px;background:${color};
      "></div>
    </div>
  `,
});

function FocusOnActive({ locations, activeId }) {
  const map = useMap();
  useEffect(() => {
    if (!map || locations.length === 0) return;
    if (activeId) {
      const l = locations.find((x) => x.id === activeId);
      if (l) map.flyTo([l.lat, l.lng], 14, { duration: 0.6 });
    }
  }, [activeId, locations, map]);
  return null;
}

function FitAllOnce({ locations }) {
  const map = useMap();
  const fittedRef = (typeof window !== "undefined") ? (window.__gomill_fitted__ = window.__gomill_fitted__ || { v: false }) : { v: false };
  useEffect(() => {
    if (!map || locations.length === 0 || fittedRef.v) return;
    const bounds = L.latLngBounds(locations.map((l) => [l.lat, l.lng]));
    map.fitBounds(bounds, { padding: [60, 60] });
    fittedRef.v = true;
  }, [map, locations, fittedRef]);
  return null;
}

export default function FindGoMill() {
  const [locs, setLocs] = useState([]);
  const [city, setCity] = useState("Hyderabad");
  const [type, setType] = useState("ALL");
  const [openOnly, setOpenOnly] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/locations`).then((r) => {
      setLocs(r.data.locations || []);
    }).catch(() => setLocs([]));
  }, []);

  const filtered = locs.filter((l) => {
    if (type !== "ALL" && l.type !== type) return false;
    if (openOnly && l.status !== "open") return false;
    return true;
  });

  const center = filtered.length
    ? [filtered.reduce((s, x) => s + x.lat, 0) / filtered.length, filtered.reduce((s, x) => s + x.lng, 0) / filtered.length]
    : [17.4399, 78.4983];

  return (
    <>
      {/* Hero */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <Overline className="mb-4">Find GoMill Today</Overline>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-gomill-green leading-[1.02] tracking-tight">
              We're <span className="italic">on the move</span>,<br/> closer to you.
            </h1>
            <p className="mt-5 text-lg text-gomill-muted max-w-xl">Come say hi at our Madhapur kiosk — more locations opening soon. Fresh. Guilt-free. Near you.</p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-2">
            <Pill tone="leaf">Live Tracking</Pill>
            <Pill tone="mustard">Real-time ETA</Pill>
            <Pill tone="cream">Open Now</Pill>
          </div>
        </div>
      </Section>

      {/* Filters */}
      <section className="px-6 md:px-12 -mt-6 md:-mt-10" data-testid="find-filters">
        <div className="max-w-[1400px] mx-auto bg-white rounded-3xl p-4 md:p-6 border border-gomill-border shadow-sm flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 text-gomill-leaf font-semibold">
            <Filter className="w-4 h-4" /> Filters
          </div>
          <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="filter-city" className="rounded-full px-4 py-2 bg-gomill-cream border border-gomill-border text-sm font-semibold text-gomill-green">
            <option>Hyderabad</option>
          </select>
          <div className="inline-flex rounded-full bg-gomill-cream p-1 border border-gomill-border">
            {["ALL", "TRUCK", "KIOSK"].map((t) => (
              <button
                key={t}
                onClick={() => setType(t)}
                data-testid={`filter-type-${t.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold ${type === t ? "bg-gomill-green text-white" : "text-gomill-green"}`}
              >
                {t === "ALL" ? "All" : t === "TRUCK" ? "Trucks" : "Kiosks"}
              </button>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 ml-auto text-sm text-gomill-green font-semibold cursor-pointer">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} data-testid="filter-open-only" className="accent-gomill-green w-4 h-4" />
            Open now
          </label>
        </div>
      </section>

      {/* Map + List */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* List */}
          <div className="lg:col-span-5 order-2 lg:order-1 flex flex-col gap-4 max-h-[700px] overflow-y-auto pr-1" data-testid="locations-list">
            {filtered.length === 0 && (
              <div className="bg-white border border-gomill-border rounded-3xl p-8 text-center">
                <p className="font-serif text-2xl text-gomill-green">No GoMills match these filters.</p>
                <p className="text-gomill-muted mt-2">Try changing the type or area.</p>
              </div>
            )}
            {filtered.map((l) => (
              <button
                key={l.id}
                onClick={() => setActive(l.id)}
                data-testid={`location-card-${l.id}`}
                className={`text-left p-5 rounded-3xl border transition-all ${
                  active === l.id
                    ? "bg-gomill-green text-white border-gomill-green shadow-lg"
                    : "bg-white border-gomill-border hover:border-gomill-green"
                }`}
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-xl ${active === l.id ? "bg-gomill-mustard text-gomill-green" : "bg-gomill-mustard/20 text-gomill-green"}`}>
                      {l.type === "TRUCK" ? <Truck className="w-4 h-4" /> : <Store className="w-4 h-4" />}
                    </span>
                    <div className="text-[10px] uppercase tracking-widest font-bold">{l.type}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ${
                    l.status === "open"
                      ? (active === l.id ? "bg-gomill-mustard text-gomill-green" : "bg-gomill-leaf/15 text-gomill-leaf")
                      : "bg-gomill-text/10 text-gomill-text"
                  }`}>
                    {l.status === "open" ? "Open Now" : "Closed"}
                  </span>
                </div>
                <h3 className={`font-serif text-xl leading-tight ${active === l.id ? "text-white" : "text-gomill-green"}`}>{l.name}</h3>
                <p className={`text-sm mt-1 ${active === l.id ? "text-white/80" : "text-gomill-muted"}`}>{l.address}</p>
                <div className={`mt-4 flex items-center justify-between text-sm ${active === l.id ? "text-white/80" : "text-gomill-muted"}`}>
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {l.open_at}–{l.close_at}</span>
                  {l.eta_minutes && <span className="font-bold">ETA · {l.eta_minutes} min</span>}
                </div>
              </button>
            ))}
          </div>

          {/* Leaflet Map */}
          <div className="lg:col-span-7 order-1 lg:order-2 relative">
            <div className="relative aspect-[4/3] lg:aspect-auto lg:h-[700px] rounded-3xl overflow-hidden border border-gomill-border bg-gomill-cream" data-testid="leaflet-map">
              <MapContainer
                center={center}
                zoom={11}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://carto.com/">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                />
                <FitAllOnce locations={filtered} />
                <FocusOnActive locations={filtered} activeId={active} />
                {filtered.map((l) => {
                  const isActive = active === l.id;
                  const color = l.type === "TRUCK" ? "#0F3824" : "#2D5A3F";
                  return (
                    <Marker
                      key={l.id}
                      position={[l.lat, l.lng]}
                      icon={makeIcon(color, isActive)}
                      eventHandlers={{ click: () => setActive(l.id) }}
                    >
                      <Popup>
                        <div className="min-w-[180px]" data-testid={`map-popup-${l.id}`}>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-gomill-leaf">{l.type}</div>
                          <div className="font-serif text-base text-gomill-green leading-tight mt-1">{l.name}</div>
                          <div className="text-xs text-gomill-muted mt-1">{l.address}</div>
                          <div className="mt-2 flex items-center justify-between text-xs">
                            <span className={`font-bold ${l.status === "open" ? "text-gomill-leaf" : "text-gomill-muted"}`}>
                              {l.status === "open" ? "Open Now" : "Closed"}
                            </span>
                            {l.eta_minutes && <span className="text-gomill-muted">ETA {l.eta_minutes} min</span>}
                          </div>
                          <a
                            href={`https://www.openstreetmap.org/directions?from=&to=${l.lat}%2C${l.lng}`}
                            target="_blank" rel="noreferrer"
                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-gomill-green hover:text-gomill-leaf"
                          >
                            <MapPin className="w-3.5 h-3.5" /> Directions
                          </a>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>

              {/* Map legend */}
              <div className="absolute bottom-4 left-4 z-[400] bg-white/95 backdrop-blur rounded-2xl border border-gomill-border px-4 py-3 text-xs">
                <div className="font-bold text-gomill-green mb-2">Hyderabad — Live</div>
                <div className="flex items-center gap-2 text-gomill-muted mb-1">
                  <span className="w-3 h-3 rounded-full bg-gomill-green border-2 border-white shadow" /> Trucks
                </div>
                <div className="flex items-center gap-2 text-gomill-muted">
                  <span className="w-3 h-3 rounded-full bg-gomill-leaf border-2 border-white shadow" /> Kiosks
                </div>
              </div>

              {active && (() => {
                const l = filtered.find((x) => x.id === active);
                if (!l) return null;
                return (
                  <div className="absolute right-4 top-4 z-[400] max-w-xs bg-white rounded-2xl border border-gomill-border p-4 shadow-xl">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-gomill-leaf">{l.type}</div>
                    <div className="font-serif text-lg text-gomill-green mt-1 leading-tight">{l.name}</div>
                    <div className="text-xs text-gomill-muted mt-1">{l.address}</div>
                    <div className="mt-3 flex items-center gap-2">
                      <a
                        href={`https://www.openstreetmap.org/directions?from=&to=${l.lat}%2C${l.lng}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-gomill-green hover:text-gomill-leaf">
                        <MapPin className="w-3.5 h-3.5" /> Directions
                      </a>
                      <a href="/menu" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-gomill-mustard text-gomill-green">
                        Order <ArrowRight className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
